import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewAPI } from '../services/api';

function InterviewStatistics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('statistics');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsHistory, setAnalyticsHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [generatingAnalytics, setGeneratingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchStatistics();
    fetchAnalyticsHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await interviewAPI.getStatistics(id);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(error.response?.data?.message || 'Failed to load statistics');
    }
    setLoading(false);
  };

  const fetchAnalyticsHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await interviewAPI.getAnalyticsHistory(id);
      setAnalyticsHistory(response.data.analyticsHistory || []);
      if (response.data.latestAnalytics) {
        setAnalytics(response.data.latestAnalytics);
      }
    } catch (error) {
      console.error('Error fetching analytics history:', error);
    }
    setLoadingHistory(false);
  };

  const generateAnalyticsReport = async () => {
    setGeneratingAnalytics(true);
    setAnalyticsError(null);
    try {
      const response = await interviewAPI.generateAnalytics(id);
      setAnalytics(response.data.analytics);
      // Refresh history
      await fetchAnalyticsHistory();
    } catch (error) {
      console.error('Error generating analytics:', error);
      setAnalyticsError(error.response?.data?.message || 'Failed to generate analytics');
    }
    setGeneratingAnalytics(false);
  };

  const loadAnalyticsReport = async (analyticsId) => {
    try {
      const response = await interviewAPI.getAnalyticsReport(id, analyticsId);
      setAnalytics(response.data.analytics);
      setShowHistory(false);
    } catch (error) {
      console.error('Error loading analytics report:', error);
      alert('Failed to load analytics report');
    }
  };

  const deleteAnalyticsReport = async (analyticsId) => {
    if (!window.confirm('Are you sure you want to delete this analytics report?')) {
      return;
    }
    
    try {
      await interviewAPI.deleteAnalyticsReport(id, analyticsId);
      // Refresh history
      await fetchAnalyticsHistory();
      // If deleted report was being displayed, clear it
      if (analytics?.id === analyticsId) {
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Error deleting analytics report:', error);
      alert('Failed to delete analytics report');
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = '#667eea', trend }) => (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'default'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: '#718096', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </div>
        {icon && (
          <div style={{ fontSize: '24px', opacity: 0.8 }}>
            {icon}
          </div>
        )}
      </div>
      <div style={{ fontSize: '32px', fontWeight: 700, color: color, marginBottom: '4px' }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: '13px', color: '#a0aec0' }}>
          {subtitle}
        </div>
      )}
      {trend && (
        <div style={{ 
          fontSize: '12px', 
          color: trend.positive ? '#38a169' : '#e53e3e',
          marginTop: '8px',
          fontWeight: 600
        }}>
          {trend.positive ? '↑' : '↓'} {trend.value}
        </div>
      )}
    </div>
  );

  const ScoreDistributionBar = ({ distribution }) => {
    const total = Object.values(distribution).reduce((sum, val) => sum + val, 0);
    
    const categories = [
      { key: 'excellent', label: '90-100', color: '#38a169', emoji: '🌟' },
      { key: 'good', label: '80-89', color: '#48bb78', emoji: '✅' },
      { key: 'average', label: '70-79', color: '#f59e0b', emoji: '👍' },
      { key: 'belowAverage', label: '60-69', color: '#f97316', emoji: '⚠️' },
      { key: 'poor', label: '0-59', color: '#e53e3e', emoji: '❌' }
    ];

    return (
      <div style={{
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '18px', fontWeight: 600 }}>
          📊 Score Distribution
        </h3>
        
        {/* Visual bar chart */}
        <div style={{ marginBottom: '24px', background: '#f7fafc', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', height: '200px', alignItems: 'flex-end', gap: '12px' }}>
            {categories.map(cat => {
              const count = distribution[cat.key] || 0;
              const percentage = total > 0 ? (count / total) * 100 : 0;
              const height = percentage;
              
              return (
                <div key={cat.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '100%',
                    height: `${height}%`,
                    maxHeight: '100%',
                    background: cat.color,
                    borderRadius: '6px 6px 0 0',
                    transition: 'height 0.3s',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: '8px',
                    minHeight: count > 0 ? '30px' : '0'
                  }}>
                    {count > 0 && (
                      <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>
                        {count}
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', fontSize: '11px', color: '#718096', fontWeight: 600 }}>
                    {cat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend with details */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          {categories.map(cat => {
            const count = distribution[cat.key] || 0;
            const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
            
            return (
              <div key={cat.key} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: '#f7fafc',
                borderRadius: '6px',
                border: `2px solid ${cat.color}20`
              }}>
                <span style={{ fontSize: '20px' }}>{cat.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#718096' }}>{cat.label}</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: cat.color }}>
                    {count} <span style={{ fontSize: '12px', fontWeight: 400 }}>({percentage}%)</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading statistics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#e53e3e', marginBottom: '20px' }}>{error}</p>
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stats = statistics;
  const interview = stats?.interview;
  const participation = stats?.participation;
  const scores = stats?.scores;
  const time = stats?.time;

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '26px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                📈 Interview Statistics & Analytics
              </h2>
              <h3 style={{ color: '#2d3748', fontSize: '18px', margin: '8px 0' }}>
                {interview?.title}
              </h3>
              {interview?.description && (
                <p style={{ color: '#718096', fontSize: '14px', margin: '4px 0' }}>
                  {interview.description}
                </p>
              )}
            </div>
            <button onClick={() => navigate(-1)} className="btn btn-secondary">
              ← Back to Interviews
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '4px', 
          borderBottom: '2px solid #e2e8f0', 
          marginBottom: '32px' 
        }}>
          <button
            onClick={() => setActiveTab('statistics')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'statistics' ? '#667eea' : 'transparent',
              color: activeTab === 'statistics' ? 'white' : '#718096',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '15px'
            }}
          >
            📊 Statistics
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'analytics' ? '#667eea' : 'transparent',
              color: activeTab === 'analytics' ? 'white' : '#718096',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '15px'
            }}
          >
            🤖 AI Analytics
          </button>
        </div>

        {/* Statistics Tab Content */}
        {activeTab === 'statistics' && (
          <>
        {/* Quick Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          <StatCard
            title="Total Participants"
            value={participation?.totalParticipants || 0}
            subtitle={`Out of ${participation?.totalAssigned || 0} assigned`}
            icon="👥"
            color="#667eea"
          />
          <StatCard
            title="Total Submissions"
            value={participation?.totalSubmissions || 0}
            subtitle={`${participation?.averageAttemptsPerStudent || 0} avg per student`}
            icon="📝"
            color="#38a169"
          />
          <StatCard
            title="Completion Rate"
            value={`${participation?.completionRate || 0}%`}
            subtitle={`${participation?.notAttempted || 0} not attempted`}
            icon="✅"
            color={participation?.completionRate >= 80 ? '#38a169' : participation?.completionRate >= 60 ? '#f59e0b' : '#e53e3e'}
          />
          <StatCard
            title="Average Score"
            value={`${scores?.average || 0}%`}
            subtitle={`Median: ${scores?.median || 0}%`}
            icon="🎯"
            color={scores?.average >= 80 ? '#38a169' : scores?.average >= 60 ? '#f59e0b' : '#e53e3e'}
          />
        </div>

        {/* Time Statistics - Prominent Display */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '32px',
          color: 'white',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontSize: '32px' }}>⏱️</span>
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 600 }}>
              Total Time Spent by All Users
            </h3>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Combined Total Time
              </div>
              <div style={{ fontSize: '36px', fontWeight: 700 }}>
                {time?.totalFormatted || '0h 0m'}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
                ({time?.totalMinutes || 0} minutes)
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Average per Attempt
              </div>
              <div style={{ fontSize: '36px', fontWeight: 700 }}>
                {time?.averagePerAttemptFormatted || '0m 0s'}
              </div>
              <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
                ({time?.averagePerAttemptMinutes || 0} minutes)
              </div>
            </div>

            <div>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                Expected Duration
              </div>
              <div style={{ fontSize: '36px', fontWeight: 700 }}>
                {interview?.duration || 0}m
              </div>
              <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
                per interview
              </div>
            </div>
          </div>
        </div>

        {/* Score Statistics */}
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '18px', fontWeight: 600 }}>
            🎯 Score Analytics
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', textTransform: 'uppercase' }}>
                Highest Score
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#38a169' }}>
                {scores?.highest || 0}%
              </div>
            </div>
            
            <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', textTransform: 'uppercase' }}>
                Average Score
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#667eea' }}>
                {scores?.average || 0}%
              </div>
            </div>
            
            <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', textTransform: 'uppercase' }}>
                Median Score
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#9f7aea' }}>
                {scores?.median || 0}%
              </div>
            </div>
            
            <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', textTransform: 'uppercase' }}>
                Lowest Score
              </div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#e53e3e' }}>
                {scores?.lowest || 0}%
              </div>
            </div>
          </div>
        </div>

        {/* Score Distribution */}
        <div style={{ marginBottom: '24px' }}>
          <ScoreDistributionBar distribution={scores?.distribution || {}} />
        </div>

        {/* Participation Breakdown */}
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '18px', fontWeight: 600 }}>
            👥 Participation Breakdown
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
            <div style={{
              padding: '16px',
              background: '#e6fffa',
              borderRadius: '8px',
              border: '2px solid #38a169'
            }}>
              <div style={{ fontSize: '12px', color: '#2c7a7b', marginBottom: '4px' }}>Submitted</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#38a169' }}>
                {participation?.totalSubmissions || 0}
              </div>
            </div>
            
            <div style={{
              padding: '16px',
              background: '#fef3c7',
              borderRadius: '8px',
              border: '2px solid #f59e0b'
            }}>
              <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>In Progress</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
                {participation?.inProgress || 0}
              </div>
            </div>
            
            <div style={{
              padding: '16px',
              background: '#fee2e2',
              borderRadius: '8px',
              border: '2px solid #e53e3e'
            }}>
              <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '4px' }}>Not Started</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#e53e3e' }}>
                {participation?.notStarted || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Submissions */}
        {stats?.recentSubmissions && stats.recentSubmissions.length > 0 && (
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#2d3748', fontSize: '18px', fontWeight: 600 }}>
              🕐 Recent Submissions
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stats.recentSubmissions.map((submission, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    background: '#f7fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '4px' }}>
                      {submission.studentName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>
                      {submission.studentEmail}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>
                        Attempt
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#667eea' }}>
                        #{submission.attemptNumber}
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>
                        Score
                      </div>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: 700,
                        color: submission.score >= 80 ? '#38a169' : submission.score >= 60 ? '#f59e0b' : '#e53e3e'
                      }}>
                        {submission.score}%
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'right', minWidth: '120px' }}>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {new Date(submission.submittedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </>
        )}

        {/* Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <div>
            {!analytics ? (
              <div style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '48px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '24px' }}>🤖</div>
                <h3 style={{ color: '#2d3748', fontSize: '22px', marginBottom: '16px', fontWeight: 600 }}>
                  AI-Powered Interview Analytics
                </h3>
                <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px', maxWidth: '600px', margin: '0 auto 32px' }}>
                  Generate a comprehensive AI analysis of all interview responses. 
                  Get insights on performance trends, common strengths and weaknesses, 
                  question difficulty analysis, and actionable recommendations.
                </p>
                
                {analyticsError && (
                  <div style={{
                    background: '#fed7d7',
                    border: '1px solid #fc8181',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    color: '#742a2a'
                  }}>
                    {analyticsError}
                  </div>
                )}

                <button
                  onClick={generateAnalyticsReport}
                  disabled={generatingAnalytics}
                  className="btn btn-primary"
                  style={{
                    padding: '14px 32px',
                    fontSize: '16px',
                    background: generatingAnalytics ? '#cbd5e0' : '#667eea',
                    cursor: generatingAnalytics ? 'not-allowed' : 'pointer'
                  }}
                >
                  {generatingAnalytics ? (
                    <>
                      <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', marginRight: '8px' }}>⚙️</span>
                      Generating Analytics... This may take a minute
                    </>
                  ) : (
                    <>
                      🚀 Generate AI Analytics Report
                    </>
                  )}
                </button>

                <div style={{ marginTop: '24px', fontSize: '13px', color: '#a0aec0' }}>
                  {statistics?.participation?.totalSubmissions > 0 ? (
                    `Will analyze ${statistics.participation.totalSubmissions} submitted response${statistics.participation.totalSubmissions !== 1 ? 's' : ''}`
                  ) : (
                    'No submitted responses to analyze'
                  )}
                </div>
              </div>
            ) : (
              <div>
                {/* Analytics Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: 600 }}>
                        🤖 AI Analytics Report
                      </h3>
                      <div style={{ fontSize: '14px', opacity: 0.9 }}>
                        Generated on {new Date(analytics.generatedAt).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
                        Analyzed {analytics.responsesAnalyzed} submitted response{analytics.responsesAnalyzed !== 1 ? 's' : ''} • 
                        AI Model: {analytics.aiModelUsed === 'deepseek' ? 'DeepSeek' : 'Gemini'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {analyticsHistory.length > 1 && (
                        <button
                          onClick={() => setShowHistory(!showHistory)}
                          style={{
                            padding: '10px 20px',
                            background: 'rgba(255,255,255,0.2)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '8px',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 600,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                          }}
                        >
                          📜 {showHistory ? 'Hide' : 'View'} History ({analyticsHistory.length})
                        </button>
                      )}
                      <button
                        onClick={generateAnalyticsReport}
                        disabled={generatingAnalytics}
                        style={{
                          padding: '10px 20px',
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '8px',
                          color: 'white',
                          cursor: generatingAnalytics ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!generatingAnalytics) {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                        }}
                      >
                        {generatingAnalytics ? '⚙️ Regenerating...' : '🔄 Regenerate'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Analytics History */}
                {showHistory && analyticsHistory.length > 0 && (
                  <div style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#2d3748', fontSize: '18px', fontWeight: 600 }}>
                      📜 Analytics History
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {analyticsHistory.map((report) => (
                        <div
                          key={report.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px',
                            background: analytics.id === report.id ? '#f0f4ff' : '#f7fafc',
                            borderRadius: '8px',
                            border: analytics.id === report.id ? '2px solid #667eea' : '1px solid #e2e8f0'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: 600, 
                              color: analytics.id === report.id ? '#667eea' : '#2d3748',
                              marginBottom: '4px' 
                            }}>
                              {analytics.id === report.id && '✓ '}Generated on {new Date(report.generatedAt).toLocaleString()}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096' }}>
                              {report.responsesAnalyzed} responses • {report.aiModelUsed === 'deepseek' ? 'DeepSeek' : 'Gemini'}
                              {report.generatedBy && ` • by ${report.generatedBy.name}`}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {analytics.id !== report.id && (
                              <button
                                onClick={() => loadAnalyticsReport(report.id)}
                                className="btn btn-secondary"
                                style={{ padding: '8px 16px', fontSize: '13px' }}
                              >
                                👁️ View
                              </button>
                            )}
                            <button
                              onClick={() => deleteAnalyticsReport(report.id)}
                              className="btn btn-danger"
                              style={{ padding: '8px 16px', fontSize: '13px' }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analytics Report Content */}
                <div style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '32px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  lineHeight: '1.8'
                }}>
                  <div 
                    style={{
                      color: '#2d3748',
                      fontSize: '15px'
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: (() => {
                        let html = analytics.report;
                        
                        // Step 1: Handle headers
                        html = html.replace(/^## (.+?)$/gm, '<h2 style="color: #667eea; font-size: 20px; margin: 32px 0 16px 0; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">$1</h2>');
                        html = html.replace(/^### (.+?)$/gm, '<h3 style="color: #4a5568; font-size: 18px; margin: 24px 0 12px 0;">$1</h3>');
                        html = html.replace(/^#### (.+?)$/gm, '<h4 style="color: #4a5568; font-size: 16px; margin: 20px 0 10px 0;">$1</h4>');
                        
                        // Step 2: Handle bold text
                        html = html.replace(/\*\*(.+?)\*\*/g, '<strong style="color: #667eea;">$1</strong>');
                        
                        // Step 3: Process lists - handle both bullet points and numbered lists
                        // First, identify and wrap bullet lists
                        html = html.replace(/((?:^- .+$\n?)+)/gm, (match) => {
                          const items = match.trim().split('\n').map(line => {
                            const content = line.replace(/^- /, '').trim();
                            return `<li style="margin: 8px 0; padding-left: 8px;">${content}</li>`;
                          }).join('\n');
                          return `<ul style="margin: 16px 0; padding-left: 24px; list-style-type: disc;">${items}</ul>`;
                        });
                        
                        // Handle numbered lists
                        html = html.replace(/((?:^\d+\. .+$\n?)+)/gm, (match) => {
                          const items = match.trim().split('\n').map(line => {
                            const content = line.replace(/^\d+\. /, '').trim();
                            return `<li style="margin: 8px 0; padding-left: 8px;">${content}</li>`;
                          }).join('\n');
                          return `<ol style="margin: 16px 0; padding-left: 24px;">${items}</ol>`;
                        });
                        
                        // Step 4: Handle paragraphs - convert double newlines to paragraph breaks
                        html = html.replace(/\n\n+/g, '</p><p style="margin: 12px 0;">');
                        html = '<p style="margin: 12px 0;">' + html + '</p>';
                        
                        // Step 5: Clean up any remaining single newlines (within paragraphs)
                        html = html.replace(/\n/g, '<br/>');
                        
                        // Step 6: Fix empty paragraphs
                        html = html.replace(/<p[^>]*>\s*<\/p>/g, '');
                        
                        return html;
                      })()
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => {
                      const reportText = analytics.report;
                      const blob = new Blob([reportText], { type: 'text/markdown' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${interview?.title || 'Interview'}_Analytics_Report_${new Date().toISOString().split('T')[0]}.md`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    className="btn btn-secondary"
                    style={{ padding: '10px 24px' }}
                  >
                    📥 Download Report (Markdown)
                  </button>
                  <button
                    onClick={() => {
                      // Convert markdown to HTML for printing
                      let html = analytics.report;
                      
                      // Handle headers
                      html = html.replace(/^## (.+?)$/gm, '<h2>$1</h2>');
                      html = html.replace(/^### (.+?)$/gm, '<h3>$1</h3>');
                      html = html.replace(/^#### (.+?)$/gm, '<h4>$1</h4>');
                      
                      // Handle bold text
                      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                      
                      // Handle bullet lists
                      html = html.replace(/((?:^- .+$\n?)+)/gm, (match) => {
                        const items = match.trim().split('\n').map(line => {
                          const content = line.replace(/^- /, '').trim();
                          return `<li>${content}</li>`;
                        }).join('\n');
                        return `<ul>${items}</ul>`;
                      });
                      
                      // Handle numbered lists
                      html = html.replace(/((?:^\d+\. .+$\n?)+)/gm, (match) => {
                        const items = match.trim().split('\n').map(line => {
                          const content = line.replace(/^\d+\. /, '').trim();
                          return `<li>${content}</li>`;
                        }).join('\n');
                        return `<ol>${items}</ol>`;
                      });
                      
                      // Handle paragraphs
                      html = html.replace(/\n\n+/g, '</p><p>');
                      html = '<p>' + html + '</p>';
                      html = html.replace(/\n/g, '<br/>');
                      html = html.replace(/<p[^>]*>\s*<\/p>/g, '');
                      
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Analytics Report - ${interview?.title}</title>
                            <style>
                              body { font-family: Arial, sans-serif; line-height: 1.8; padding: 40px; max-width: 900px; margin: 0 auto; }
                              h1 { color: #667eea; border-bottom: 3px solid #667eea; padding-bottom: 10px; }
                              h2 { color: #667eea; margin-top: 32px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; font-size: 20px; }
                              h3 { color: #4a5568; margin-top: 24px; font-size: 18px; }
                              h4 { color: #4a5568; margin-top: 20px; font-size: 16px; }
                              strong { color: #667eea; }
                              ul, ol { margin: 16px 0; padding-left: 24px; }
                              ul { list-style-type: disc; }
                              li { margin: 8px 0; }
                              p { margin: 12px 0; }
                            </style>
                          </head>
                          <body>
                            <h1>AI Analytics Report: ${interview?.title}</h1>
                            <p><em>Generated on ${new Date(analytics.generatedAt).toLocaleString()}</em></p>
                            ${html}
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="btn btn-secondary"
                    style={{ padding: '10px 24px' }}
                  >
                    🖨️ Print Report
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default InterviewStatistics;
